/* eslint-disable camelcase */
import * as React from 'react';
import { useState } from 'react';
import { useEffect } from 'react';
import { CompareResultObject } from '../../models/CompareResultObject';
import { CompareValueType } from '../../store/HawkState';
import { useHawkSearch } from '../StoreProvider';

//#region Commerce
import ProductPricing from 'shared/components/commerce/ProductPricing';
//#endregion Commerce

//#region HCL
import CompareProductAddToCart from 'features/search/components/commerce/CompareProductAddToCart.HCL';
import { GetProductComparisonPriceDetails } from '../ui/Results/data.HCL';
import Spinner from '../ui/Spinner';
//#endregion

interface CompareItemHeader {
	docId: string;
	name: string;
	url: string;
	imageUrl?: string;
	imageAltTag?: string;
}

/**
 * A table of products that are being compared
 * @param props
 */
// #region HCL - this entire widget is customized to override prices from Hawk with prices from ROC
export default function CompareItemsTableHCL(props: { compareItems: CompareResultObject[] }) {
	const [isLoadingPricing, setIsLoadingPricing] = useState(false);

	useEffect(() => {
		if (props.compareItems) {
			setIsLoadingPricing(true);

			(async () => {
				try {
					const response = await GetProductComparisonPriceDetails(props.compareItems);
					if (response && response.productsPrices && response.productsPrices.length > 0) {
						// add overridden pricing to items for display
						props.compareItems.forEach((ci) => {
							const skus = ci.Document.find((item) => item.Field === 'product_sku');
							if (skus && skus.Value.length > 0) {
								const sku = skus.Value[0];

								const matchingCustomPrice = response.productsPrices.find((p) => p.sku === sku);

								if (sku && matchingCustomPrice) {
									ci.Document.push({
										Compare: true,
										Field: 'customer_specific_pricing',
										Label: 'Display Pricing',
										Value: [matchingCustomPrice.price],
									});
								}
							}
						});
					}
				} catch (error) {
					// Handle error if necessary
				} finally {
					setIsLoadingPricing(false);
				}
			})();
		}
	}, [props.compareItems]);

	return (
		<>
			<Spinner isVisible={isLoadingPricing} />
			{!isLoadingPricing ? (
				<div className="roc-manage-table">
					<div className="roc-table-responsive">
						<table className="roc-manage-table__table">
							<CompareTableHeaders compareItems={props.compareItems} />
							<CompareTableBody compareItems={props.compareItems} />
						</table>
					</div>
				</div>
			) : null}
		</>
	);
}
// #endregion HCL

/**
 * Top of the compare items table which displays a product's image and name
 * @param props
 */
function CompareTableHeaders(props: { compareItems: CompareResultObject[] }) {
	const {
		actor: { getCompareFieldValue },
	} = useHawkSearch();

	const [itemHeaders, setItemHeaders] = useState<CompareItemHeader[]>([]);

	useEffect(() => {
		setItemHeaders(
			props.compareItems.map((item) => {
				const name = getCompareFieldValue(item, 'name', CompareValueType.SingleValueString);
				const url = getCompareFieldValue(item, 'url_detail', CompareValueType.SingleValueString);
				const imageUrl = getCompareFieldValue(item, 'image', CompareValueType.SingleValueString);
				const imageAltTag = getCompareFieldValue(
					item,
					'product_imagealttag',
					CompareValueType.SingleValueString,
				);

				return {
					docId: item.DocId,
					name: name ?? 'N/A',
					url: url ?? '/',
					imageUrl: imageUrl ?? '/images/image-not-found.png',
					imageAltTag: imageAltTag ?? name ?? 'N/A',
				};
			}),
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.compareItems]);

	return (
		<thead className="roc-manage-table__thead roc-manage-table__thead--align-top">
			<tr className="roc-manage-table__row">
				<th className="roc-manage-table__th" />
				{itemHeaders.map((item) => {
					return (
						<th key={`header-${item.docId}`} className="roc-manage-table__th">
							<a
								href={item.url}
								className="roc-manage-table__td-link roc-manage-table__td-link--inline-block"
							>
								<img
									src={item.imageUrl}
									alt={item.imageAltTag}
									className="roc-manage-table__img roc-manage-table__img--md"
								/>
								<div>{item.name}</div>
							</a>
						</th>
					);
				})}
			</tr>
		</thead>
	);
}

/** Hawksearch fields that should be included in the compare table */
const COMPARE_KEYS = [
	'product_brand',
	'product_displayprice', // JSON value, requires JSON parsing
	'product_groupdisplayprice', // Internal use only. Gets mapped to product_displayprice for the client
	'product_ratingaverage', // Decimal value, requires float parsing
	'product_sku',
	'productattr_c',
	'productattr_size',
	// #region HCL
	'customer_specific_pricing', // Internal use only, if loaded from ajax call, gets mapped to product_displayprice for the client
	'productattr_adacompliant',
	'productattr_adhesivetype',
	'productattr_autoclavable',
	'productattr_cdccompliant',
	'productattr_chemoclean',
	'productattr_hclexclusiveitem',
	'productattr_productlength',
	'productattr_productheight',
	'productattr_productwidth',
	'productattr_sealsremovalable',
	'productattr_sterile',
	'productattr_supplierpartno',
	'productattr_tempmeasurement',
	// #endregion
];

/**
 * Table body for compare items which lists the values of various attributes to be compared among the products
 * @param props
 */
function CompareTableBody(props: { compareItems: CompareResultObject[] }) {
	const {
		actor: { getCompareFieldValue },
	} = useHawkSearch();

	const [compareFields, setCompareFields] = useState<Record<string, any>>({});
	const [compareValues, setCompareValues] = useState<Record<string, string>[]>([]);

	const { compareItems } = props;

	useEffect(() => {
		const distinctFields = {};

		const valuesResult: Record<string, string>[] = [];
		for (const item of compareItems) {
			const valuesItem = { docId: item.DocId };

			// Create a set of all distinct fields that should be included
			for (const fieldItem of item.Document) {
				if (fieldItem.Compare && COMPARE_KEYS.includes(fieldItem.Field)) {
					if (distinctFields[fieldItem.Field] == null) {
						distinctFields[fieldItem.Field] = fieldItem.Label;
					}
				}
			}

			// Loop through the distinct fields and get their value if present
			for (const key of Object.keys(distinctFields)) {
				switch (key) {
					case 'product_ratingaverage':
						valuesItem[key] = getCompareFieldValue(item, key, CompareValueType.MultipleValueDecimal);
						break;
					case 'product_displayprice':
					case 'product_groupdisplayprice':
					case 'customer_specific_pricing':
						valuesItem[key] = getCompareFieldValue(item, key, CompareValueType.SingleValueString);
						break;
					default:
						valuesItem[key] = getCompareFieldValue(item, key, CompareValueType.MultipleValueString);
				}
			}

			valuesResult.push(valuesItem);
		}

		// Set group products' prices to regular price field
		if (distinctFields['product_groupdisplayprice'] !== undefined) {
			for (const value of valuesResult) {
				if (value['product_groupdisplayprice'] !== undefined) {
					value['product_displayprice'] = value['product_groupdisplayprice'];
				}
			}

			// Now delete group product price field since it has been mapped to non-group price field
			delete distinctFields['product_groupdisplayprice'];
		}

		// Set products' prices to customer specific price field
		if (distinctFields['customer_specific_pricing'] !== undefined) {
			for (const value of valuesResult) {
				if (value['customer_specific_pricing'] !== undefined) {
					value['product_displayprice'] = value['customer_specific_pricing'];
				}
			}

			// Now delete group product price field since it has been mapped to non-group price field
			delete distinctFields['customer_specific_pricing'];
		}

		setCompareFields(distinctFields);
		setCompareValues(valuesResult);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [compareItems]);

	return (
		<tbody className="roc-manage-table__tbody">
			{Object.keys(compareFields).map((field) => {
				const label = compareFields[field];

				return (
					<tr key={`row-${field}`} className="roc-manage-table__row">
						<td className="roc-manage-table__td">
							<b>{label}</b>
						</td>
						{compareValues.map((value) => {
							return (
								<td key={`value-${label}-${value.docId}`} className="roc-manage-table__td">
									<CompareItemFieldValue value={value[field]} field={field} />
								</td>
							);
						})}
					</tr>
				);
			})}
			{/* #region HCL */}
			<tr className="roc-manage-table__row">
				<td className="roc-manage-table__td"></td>
				{compareItems.map((item) => {
					const exists = item.Document.filter(obj => obj['Field'] === 'product_groupdisplayprice').length > 0;
					if(!exists) {
						return (																							
							<td key={`add-to-cart-${item.DocId}`} className="roc-manage-table__td">
								<CompareProductAddToCart compareResultItem={item} />
							</td>
							);
						} else {
							return (
								<td key={`add-to-cart-${item.DocId}`} className="roc-manage-table__td">			
								</td>
							);
						}					
				})}
			</tr>
			{/* #endregion HCL */}
		</tbody>
	);
}

/**
 * The value for a field. Displays 'N/A' if the field's value is not present for this item
 * @param props
 */
function CompareItemFieldValue(props: { value: string | null; field: string }) {
	const { value } = props;

	if (value == null) {
		return <>{'N/A'}</>;
	}

	//#region Commerce
	if (props.field === 'product_displayprice') {
		return <ProductPricing displayPrice={JSON.parse(value)} />;
	}
	//#endregion Commerce

	return <>{value}</>;
}
